import { Link } from "react-router-dom";
import { LegalPage } from "@/components/LegalPage";
import { Placeholder } from "@/components/Placeholder";
import { BUSINESS, CONTACT } from "@/data/content";

const P = ({ children }) => <p className="mb-3 last:mb-0">{children}</p>;

const SECTIONS = [
  {
    heading: "No Online Checkout",
    body: (
      <P>
        This website does not take card payments or process orders online. All services are purchased by accepting a written
        quotation and paying the invoices we issue. This page explains how that works so you know what to expect before you commit.
      </P>
    ),
  },
  {
    heading: "What You See Before You Pay",
    body: (
      <P>
        Every quotation states: the service name and description; the price and currency ({BUSINESS.currency.value}); exactly what is
        included and what is not; the delivery timeframe or service period; the payment milestones; and links to our{" "}
        <Link to="/terms-and-conditions" className="font-medium text-brand hover:underline">Terms & Conditions</Link>,{" "}
        <Link to="/privacy-policy" className="font-medium text-brand hover:underline">Privacy Policy</Link> and{" "}
        <Link to="/refund-policy" className="font-medium text-brand hover:underline">Refund & Cancellation Policy</Link>. Nothing is invoiced until you accept the quotation in writing.
      </P>
    ),
  },
  {
    heading: "Invoicing",
    body: (
      <P>
        Invoices are issued by email from {CONTACT.email} in the name of {CONTACT.name} (legal name <Placeholder field={BUSINESS.legalName} />). Each invoice
        references your quotation and states the amount, due date and payment methods. For one-off projects, 50% is invoiced as a deposit on acceptance of the quotation and 50% on completion; monthly
        services are invoiced in advance each month.
      </P>
    ),
  },
  {
    heading: "Accepted Payment Methods",
    body: (
      <P>
        We accept credit and debit cards (via a secure payment link issued with the invoice), bank transfer (ACH or wire) and
        PayPal. Bank details, where applicable, are printed on the invoice itself. We never ask for card details by email or telephone.
      </P>
    ),
  },
  {
    heading: "Currency and Taxes",
    body: <P>All prices are quoted in {BUSINESS.currency.value}. Applicable taxes, if any, are stated separately on the quotation and invoice.</P>,
  },
  {
    heading: "Payment Terms",
    body: (
      <P>
        Quotations are valid for 3 days from issue. Invoices are due within 3 days of the invoice date unless the quotation states
        otherwise. Work is scheduled on receipt of the deposit and final deliverables are released on receipt of the balance.
      </P>
    ),
  },
  {
    heading: "Receipts and Records",
    body: <P>A receipt is issued for every payment received. Keep your quotation, invoices and receipts — they define the scope and terms of your engagement.</P>,
  },
  {
    heading: "Questions About a Payment",
    body: <P>Contact {CONTACT.email} or {CONTACT.phone}. Refund requests are handled under our Refund & Cancellation Policy.</P>,
  },
];

export default function PaymentInformation() {
  return (
    <LegalPage
      testId="payment-page"
      overline="Payment Information"
      title="How services are purchased and paid for"
      lede="We do not sell online. Services are bought by accepting a written quotation and paying the invoices we issue. Here is what you will see before you pay and how payment works."
      seoDescription="How to purchase and pay for My Geeks LLC services: written quotations, invoicing, accepted payment methods, currency, payment terms and receipts."
      sections={SECTIONS}
    />
  );
}
